// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the desktop Rail's settings. Size: how tall the panels are. Icons:
// which panels the strip shows and in what order (both kept on this computer,
// like the Rail itself). Auto-hide
// collapses the Rail to a chevron on the screen edge; hover peeks, click pins,
// and it hides again after the delay (saved with the account).

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import RailSize, {type RailSize as RailSizeValue} from '@app/features/konvo_rail/state/RailSize';
import type {RailKey} from '@app/features/konvo_rail/state/RailState';
import RailTabs from '@app/features/konvo_rail/state/RailTabs';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {
	CalendarBlankIcon,
	CaretDownIcon,
	CaretUpIcon,
	ChatCircleIcon,
	ChecksIcon,
	NotePencilIcon,
	SidebarSimpleIcon,
	SparkleIcon,
	SunIcon,
	TimerIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import styles from '@app/features/user/components/modals/tabs/KonvoMobileRailSettingsTab.module.css';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const SHORTCUT = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘⇧K' : 'Ctrl+Shift+K';

const RAIL_ICON_LABELS: Record<RailKey, MessageDescriptor> = {
	chats: msg({message: 'Chats', comment: 'Settings > Rail: the Chats icon on the desktop Rail'}),
	today: msg({message: 'Home', comment: 'Settings > Rail: the Home icon on the desktop Rail'}),
	calendar: msg({message: 'Calendar', comment: 'Settings > Rail: the Calendar icon on the desktop Rail'}),
	tasks: msg({message: 'Tasks', comment: 'Settings > Rail: the Tasks icon on the desktop Rail'}),
	scratchpad: msg({message: 'Notes', comment: 'Settings > Rail: the Notes (scratchpad) icon on the desktop Rail'}),
	assistant: msg({message: 'Ask Konvo', comment: 'Settings > Rail: the Ask Konvo icon on the desktop Rail'}),
};

const RAIL_ICONS: Record<RailKey, React.ReactNode> = {
	chats: <ChatCircleIcon size={18} />,
	today: <SunIcon size={18} />,
	calendar: <CalendarBlankIcon size={18} />,
	tasks: <ChecksIcon size={18} />,
	scratchpad: <NotePencilIcon size={18} />,
	assistant: <SparkleIcon size={18} />,
};

const MOVE_UP = msg({message: 'Move {label} up', comment: 'Settings > Rail: moves an icon one place up the strip'});
const MOVE_DOWN = msg({
	message: 'Move {label} down',
	comment: 'Settings > Rail: moves an icon one place down the strip',
});
const SHOW_ON_RAIL = msg({
	message: 'Show {label} on the Rail',
	comment: 'Settings > Rail: toggle that shows or hides an icon on the strip',
});

const SIZE_LABELS: Record<RailSizeValue, MessageDescriptor> = {
	compact: msg({message: 'Compact', comment: 'Settings > Rail > Size: panels as tall as the icon strip'}),
	tall: msg({message: 'Tall', comment: 'Settings > Rail > Size: panels a fixed comfortable height'}),
	full: msg({message: 'Full height', comment: 'Settings > Rail > Size: panels as tall as the screen'}),
};

const SIZE_HINTS: Record<RailSizeValue, MessageDescriptor> = {
	compact: msg({
		message: 'Panels are as tall as the icons beside them. Least in the way.',
		comment: 'Settings > Rail > Size: explains the Compact option',
	}),
	tall: msg({
		message: 'Panels are a fixed height with room for a chat or your task list.',
		comment: 'Settings > Rail > Size: explains the Tall option',
	}),
	full: msg({
		message: 'Panels run from the top of the screen to the bottom. Most at a glance.',
		comment: 'Settings > Rail > Size: explains the Full height option',
	}),
};

const SIZE_LABEL = msg({message: 'Rail size', comment: 'Settings > Rail: accessible name of the size switch'});

/** How tall the Rail's panels are; every panel uses the same height. */
const RailSizeSection = observer(() => {
	const {i18n} = useLingui();
	return (
		<SettingsSection
			id="konvo-rail-size"
			title={<Trans>Size</Trans>}
			description={i18n._(SIZE_HINTS[RailSize.size])}
			linkable={false}
			data-flx="user.konvo-rail-tab.size"
		>
			<Segmented<RailSizeValue>
				value={RailSize.size}
				options={(['compact', 'tall', 'full'] as const).map((value) => ({
					value,
					children: i18n._(SIZE_LABELS[value]),
				}))}
				onChange={(value) => RailSize.set(value)}
				aria-label={i18n._(SIZE_LABEL)}
				data-flx="user.konvo-rail-tab.size.switch"
			/>
		</SettingsSection>
	);
});

/** Show, hide and reorder the strip's icons. */
const RailIconsSection = observer(() => {
	const {i18n} = useLingui();
	const {order} = RailTabs.value;
	const visible = RailTabs.visible;
	return (
		<SettingsSection
			id="konvo-rail-icons"
			title={<Trans>Icons</Trans>}
			description={<Trans>Choose the panels on the Rail and their order. Saved on this computer.</Trans>}
			linkable={false}
			data-flx="user.konvo-rail-tab.icons"
		>
			<div className={styles.rows} data-flx="user.konvo-rail-tab.icons.rows">
				{order.map((key, index) => {
					const label = i18n._(RAIL_ICON_LABELS[key]);
					const on = !RailTabs.isHidden(key);
					return (
						<div key={key} className={clsx(styles.row, !on && styles.rowOff)} data-flx="user.konvo-rail-tab.icons.row">
							<span className={styles.iconTile}>{RAIL_ICONS[key]}</span>
							<span className={styles.label}>{label}</span>
							<span className={styles.moveButtons}>
								<button
									type="button"
									className={styles.moveButton}
									disabled={index === 0}
									onClick={() => RailTabs.move(key, -1)}
									aria-label={i18n._(MOVE_UP, {label})}
									title={i18n._(MOVE_UP, {label})}
									data-flx="user.konvo-rail-tab.icons.up"
								>
									<CaretUpIcon size={16} />
								</button>
								<button
									type="button"
									className={styles.moveButton}
									disabled={index === order.length - 1}
									onClick={() => RailTabs.move(key, 1)}
									aria-label={i18n._(MOVE_DOWN, {label})}
									title={i18n._(MOVE_DOWN, {label})}
									data-flx="user.konvo-rail-tab.icons.down"
								>
									<CaretDownIcon size={16} />
								</button>
							</span>
							<Switch
								compact
								value={on}
								disabled={on && visible.length === 1}
								onChange={(next) => RailTabs.setHidden(key, !next)}
								ariaLabel={i18n._(SHOW_ON_RAIL, {label})}
								className={styles.switch}
								data-flx="user.konvo-rail-tab.icons.switch"
							/>
						</div>
					);
				})}
			</div>
		</SettingsSection>
	);
});

export const KonvoRailSettingsTab = observer(() => {
	const {t} = useLingui();
	useEffect(() => {
		void KonvoCalendarPrefs.load();
	}, []);
	const delay = KonvoCalendarPrefs.railAutoHideDelay;
	return (
		<SettingsTabContainer data-flx="user.konvo-rail-tab.container">
			<SettingsTabContent data-flx="user.konvo-rail-tab.content">
				<RailSizeSection />
				<RailIconsSection />
				<SettingsSection
					id="konvo-rail-auto-hide"
					title={<Trans>Auto-hide</Trans>}
					data-flx="user.konvo-rail-tab.section"
				>
					{!isDesktop() && (
						<p className={kitStyles.note}>
							<Trans>The Rail is part of the desktop app. These settings apply there.</Trans>
						</p>
					)}
					<div className={kitStyles.rows} data-flx="user.konvo-rail-tab.rows">
						<Switch
							label={t`Auto-hide the Rail`}
							description={t`Collapse the Rail to a small tab on the edge of the screen. Hover the tab to peek, click it to keep the Rail open.`}
							value={KonvoCalendarPrefs.railAutoHide}
							onChange={(next) => void KonvoCalendarPrefs.save({railAutoHide: next})}
							icon={<SidebarSimpleIcon size={20} />}
							data-flx="user.konvo-rail-tab.auto-hide"
						/>
						<SettingsRow
							icon={<TimerIcon size={20} />}
							title={<Trans>Hide after</Trans>}
							description={<Trans>Counted from when the pointer leaves the Rail and nothing in it has focus.</Trans>}
							disabled={!KonvoCalendarPrefs.railAutoHide}
							control={({labelId}) => (
								<span className={kitStyles.rowSlider}>
									<Slider
										value={delay}
										defaultValue={delay}
										factoryDefaultValue={5}
										minValue={2}
										maxValue={60}
										step={1}
										disabled={!KonvoCalendarPrefs.railAutoHide}
										ariaLabelledBy={labelId}
										ariaValueText={t`${delay} seconds`}
										onValueRender={(value) => t`${Math.round(value)} seconds`}
										onValueChange={(value) => void KonvoCalendarPrefs.save({railAutoHideDelay: Math.round(value)})}
										data-flx="user.konvo-rail-tab.delay"
									/>
									<span className={kitStyles.rowValue} aria-hidden="true">
										{t`${delay} seconds`}
									</span>
								</span>
							)}
							data-flx="user.konvo-rail-tab.delay-row"
						/>
					</div>
					<p className={kitStyles.note}>
						<Trans>Show or hide the Rail any time with {SHORTCUT}.</Trans>
					</p>
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default KonvoRailSettingsTab;
